import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Psychology as AIIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Science as ScienceIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Biotech as BiotechIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AILLMSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            AI and LLM Initiatives (CBP AI Program Office)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Generative AI and Machine Learning Innovation Programs
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The CBP AI Program Office leads the agency's artificial intelligence and large language model (LLM) initiatives, 
            developing cutting-edge generative AI pilots for risk detection, fentanyl interdiction, and operational efficiency. 
            The program focuses on responsible AI deployment through edge and cloud-based systems that enhance CBP's mission 
            capabilities while maintaining privacy, security, and ethical standards.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="AI LLM System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What are AI LLM Initiatives"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A portfolio of <strong>artificial intelligence and generative AI projects</strong> leveraging large language 
                    models (LLMs), computer vision, and machine learning to enhance border security operations. Initiatives include 
                    <strong> ChatCBP (internal AI assistant), fentanyl detection models, document analysis automation</strong>, and 
                    intelligent targeting systems deployed on both edge devices and cloud infrastructure.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who leads AI LLM Initiatives"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Led by the <strong>CBP AI Program Office</strong> within the Office of Information and Technology (OIT), in 
                    collaboration with DHS Science and Technology Directorate (S&T). Programs support <strong>CBP officers, analysts, 
                    and operational personnel</strong> across all mission areas. Partnerships with <strong>NIST, academic institutions, 
                    and AI vendors</strong> ensure responsible AI development and deployment.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When were AI LLM Initiatives started"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    AI Program Office established in <strong>2019</strong> to coordinate machine learning initiatives. Generative AI 
                    pilots launched in <strong>2023</strong> following ChatGPT emergence. <strong>ChatCBP pilot (2023-2024)</strong>, 
                    fentanyl detection models (2024), and document automation projects currently in development. Continuous evaluation 
                    and expansion of <strong>responsible AI use cases</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where are AI LLM systems deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Hybrid deployment model</strong> with cloud-based training and inference in AWS GovCloud, and 
                    <strong> edge AI devices</strong> at ports of entry for real-time processing. Pilot programs at <strong>select 
                    ports and National Targeting Center</strong>. Internal tools accessible to <strong>authorized personnel via secure 
                    networks</strong>. Future expansion to additional ports and operational environments pending pilot results.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why AI LLM Initiatives exist"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To leverage <strong>cutting-edge AI capabilities</strong> for enhanced threat detection, operational efficiency, 
                    and officer decision support. Addresses <strong>fentanyl crisis</strong> through advanced detection, improves 
                    <strong>targeting accuracy</strong> while reducing false positives, and automates <strong>repetitive tasks</strong> 
                    to free officers for high-value activities. Ensures CBP remains at forefront of <strong>responsible AI adoption</strong> 
                    in law enforcement.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Key AI Initiatives */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Key AI & LLM Initiatives
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoAwesomeIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Generative AI Risk Detection
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Large language models for threat intelligence and risk assessment.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>ChatCBP:</strong> Internal AI assistant for policy queries and operational guidance</Typography></li>
                <li><Typography variant="body2"><strong>Document Analysis:</strong> Automated review of travel documents and manifests</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Synthesis:</strong> LLM-based threat report summarization</Typography></li>
                <li><Typography variant="body2"><strong>Natural Language Queries:</strong> Conversational interface for database searches</Typography></li>
                <li><Typography variant="body2"><strong>Risk Narrative Generation:</strong> Automated case summaries and alerts</Typography></li>
                <li><Typography variant="body2"><strong>Multilingual Support:</strong> Translation and analysis in 100+ languages</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiotechIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Fentanyl Interdiction Models
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                AI-powered detection systems for opioid smuggling prevention.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>NII Image Analysis:</strong> Computer vision for X-ray anomaly detection</Typography></li>
                <li><Typography variant="body2"><strong>Pattern Recognition:</strong> ML models identifying smuggling methods</Typography></li>
                <li><Typography variant="body2"><strong>Concealment Detection:</strong> AI-enhanced scanning of vehicles and cargo</Typography></li>
                <li><Typography variant="body2"><strong>Behavioral Analytics:</strong> Traveler and shipper risk profiling</Typography></li>
                <li><Typography variant="body2"><strong>Chemical Signatures:</strong> Spectroscopy data analysis for substance identification</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Alerts:</strong> Edge AI for immediate officer notification</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Edge and Cloud-Based AI Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Distributed AI deployment for low-latency and scalable processing.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Edge Inference:</strong> On-device AI models at ports for real-time decisions</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Training:</strong> Large-scale model development in AWS GovCloud</Typography></li>
                <li><Typography variant="body2"><strong>Federated Learning:</strong> Privacy-preserving model training across sites</Typography></li>
                <li><Typography variant="body2"><strong>Model Optimization:</strong> Compression for edge deployment</Typography></li>
                <li><Typography variant="body2"><strong>Hybrid Architecture:</strong> Seamless cloud-edge orchestration</Typography></li>
                <li><Typography variant="body2"><strong>Offline Capability:</strong> Edge AI operates without connectivity</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Operational Efficiency Automation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                AI-driven process automation and decision support tools.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Automated Reporting:</strong> AI-generated operational summaries and metrics</Typography></li>
                <li><Typography variant="body2"><strong>Scheduling Optimization:</strong> ML-based staffing and resource allocation</Typography></li>
                <li><Typography variant="body2"><strong>Predictive Maintenance:</strong> Equipment failure forecasting</Typography></li>
                <li><Typography variant="body2"><strong>Workflow Automation:</strong> RPA with AI decision-making</Typography></li>
                <li><Typography variant="body2"><strong>Training Simulations:</strong> AI-powered scenario generation for officers</Typography></li>
                <li><Typography variant="body2"><strong>Quality Assurance:</strong> Automated review of inspection procedures</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Responsible AI Framework */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Responsible AI Framework
      </Typography>

      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            AI Ethics and Governance
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Fairness & Bias Mitigation:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Bias testing and monitoring</Typography></li>
                <li><Typography variant="body2">Diverse training data requirements</Typography></li>
                <li><Typography variant="body2">Fairness metrics and auditing</Typography></li>
                <li><Typography variant="body2">Disparate impact analysis</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Transparency & Explainability:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Model interpretability requirements</Typography></li>
                <li><Typography variant="body2">Decision explanation capabilities</Typography></li>
                <li><Typography variant="body2">Human-in-the-loop design</Typography></li>
                <li><Typography variant="body2">Audit trails for AI decisions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Privacy & Security:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Data minimization principles</Typography></li>
                <li><Typography variant="body2">Privacy-preserving AI techniques</Typography></li>
                <li><Typography variant="body2">Secure model deployment</Typography></li>
                <li><Typography variant="body2">Adversarial robustness testing</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pilot Programs */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Active Pilot Programs
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                15+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active AI Pilots
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Across CBP operations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                500+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pilot Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Officers and analysts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                10+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Partner Organizations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                DHS, NIST, academia
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Mission Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Enhanced Detection:</strong> AI improves fentanyl interdiction rates</Typography></li>
                <li><Typography variant="body2"><strong>Faster Processing:</strong> Automated document review reduces wait times</Typography></li>
                <li><Typography variant="body2"><strong>Better Targeting:</strong> ML models increase threat detection accuracy</Typography></li>
                <li><Typography variant="body2"><strong>Officer Support:</strong> AI assistants provide real-time guidance</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Predictive analytics improve staffing</Typography></li>
                <li><Typography variant="body2"><strong>Training Enhancement:</strong> AI-powered simulations for officers</Typography></li>
                <li><Typography variant="body2"><strong>Innovation Leadership:</strong> CBP at forefront of law enforcement AI</Typography></li>
                <li><Typography variant="body2"><strong>Responsible Deployment:</strong> Ethics-first approach to AI adoption</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technology Partners */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Technology Stack & Partners
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="OpenAI GPT Models" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Large language models</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="AWS SageMaker" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">ML training and deployment</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="NVIDIA Edge AI" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Real-time inference devices</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="TensorFlow/PyTorch" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Model development frameworks</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Hugging Face" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Pre-trained model library</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="MLflow" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Model lifecycle management</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Kubernetes" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Container orchestration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="NIST AI Framework" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">AI risk management</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AILLMSystemDetail;
